import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Inbox, Briefcase, Send, Clock, CheckCircle2, AlertCircle, XCircle,
  Upload, FileText, CreditCard, Receipt, Crown, ShieldCheck,
  UserCheck, Ticket, AlertTriangle, Eye, Check, X,
  Calendar, DollarSign, Plus, Image as ImageIcon,
  Users, Settings, BarChart3, Sparkles, GraduationCap, ShoppingBag,
  Heart, Video, MessageSquare, BookOpen, TrendingUp, Layers,
} from "lucide-react";
import { useRole, ROLE_META } from "@/context/RoleContext";
import { useAuth } from "@/context/AuthContext";

const consultantPhotoSrc = (value) => value && !/^https?:\/\//.test(value) ? (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + value : value;

/* ============================================================
   KD9 — Generic page shell for placeholders + several rich
   role-specific pages. All visual, no backend.
   ============================================================ */

export const PageShell = ({ title, kicker, desc, children, badge, testId }) => {
  const { role } = useRole();
  const meta = ROLE_META[role];
  return (
    <div data-testid={testId || "page-shell"}>
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div
            className="font-mont text-[11px] tracking-[0.4em] uppercase mb-2"
            style={{ color: meta.color }}
          >
            · {kicker || meta.label} ·
          </div>
          <h1 className="font-cinzel kd9-gold-text text-3xl md:text-4xl tracking-[0.06em] mb-3">
            {title}
          </h1>
          {desc && (
            <p className="font-cormorant text-lg max-w-3xl" style={{ color: "var(--kd9-text-soft)" }}>
              {desc}
            </p>
          )}
        </div>
        {badge}
      </header>
      {children}
    </div>
  );
};

const STATUS_TONES = {
  Disponible: "#3C78B5",
  Tomado: "#A855F7",
  Asignado: "#0F4C81",
  "En proceso": "#D4AF37",
  "En revisión administrativa": "#A67C00",
  "Devuelto para corrección": "#7A0E17",
  Aprobado: "#16A34A",
  Entregado: "#1FA774",
  Cancelado: "#525252",
};

const StatusBadge = ({ status }) => (
  <span
    className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold"
    style={{
      background: `${STATUS_TONES[status] || "#888"}22`,
      color: STATUS_TONES[status] || "#888",
      border: `1px solid ${STATUS_TONES[status] || "#888"}55`,
    }}
  >
    {status}
  </span>
);

/* ============== BOLSA DE CONSULTAS (Consultor · Acompañamiento — Pantalla 3) ============== */
export const BolsaConsultas = () => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [ok, setOk] = React.useState("");
  const [openId, setOpenId] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("all");
  // Draft schedule/complete forms per row id
  const [sched, setSched] = React.useState({});
  const [comp, setComp] = React.useState({});

  const reload = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/api/consultor/acompanamiento/assigned");
      setRows(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  const doAction = async (path, id, body, okMsg) => {
    setBusy(id); setError(""); setOk("");
    try {
      await api.post(`/api/consultor/acompanamiento/${id}/${path}`, body || {});
      setOk(okMsg);
      await reload();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const accept = (id) => doAction("accept", id, {}, "Acompañamiento aceptado.");
  const reject = (id) => {
    const observation = window.prompt("Escribe el motivo del rechazo para dejar trazabilidad:");
    if (observation?.trim()) doAction("reject", id, { observation: observation.trim() }, "Solicitud rechazada.");
  };
  const start = (id) => doAction("start", id, {}, "Acompañamiento iniciado.");
  const schedule = (id) => {
    const s = sched[id] || {};
    if (!s.date || !s.time || !s.medium) {
      setError("Completá fecha, hora y medio de atención.");
      return;
    }
    doAction("schedule", id, {
      scheduled_date: s.date,
      scheduled_time: s.time,
      medium: s.medium,
      contact_info: {
        zoom_link: s.zoom_link || undefined,
        meet_link: s.meet_link || undefined,
        whatsapp: s.whatsapp || undefined,
        phone: s.phone || undefined,
      },
    }, "Cita programada.");
  };
  const complete = (id) => {
    const c = comp[id] || {};
    doAction("complete", id, {
      therapy_notes: c.therapy_notes || undefined,
      recommendations: c.recommendations || undefined,
      private_notes: c.private_notes || undefined,
      final_status: c.final_status || "attention_completed",
    }, "Atención finalizada.");
  };

  const statusLabel = (s) => ({
    assigned: "Consultor asignado",
    accepted: "Aceptado",
    rejected: "Rechazado",
    scheduled: "Cita programada",
    in_progress: "En proceso",
    completed: "Atención finalizada",
    cancelled: "Cancelado",
  }[s] || s);
  const statusTone = (s) => ({
    assigned: { c: "var(--kd9-gold)", bg: "rgba(212,175,55,0.18)", bd: "rgba(212,175,55,0.4)" },
    accepted: { c: "#86EFAC", bg: "rgba(34,197,94,0.14)", bd: "rgba(34,197,94,0.4)" },
    rejected: { c: "#FCA5A5", bg: "rgba(122,14,23,0.10)", bd: "rgba(122,14,23,0.4)" },
    scheduled: { c: "#C0A555", bg: "rgba(166,124,0,0.18)", bd: "rgba(166,124,0,0.5)" },
    in_progress: { c: "#7BB8E8", bg: "rgba(15,76,129,0.20)", bd: "rgba(15,76,129,0.5)" },
    completed: { c: "#86EFAC", bg: "rgba(34,197,94,0.14)", bd: "rgba(34,197,94,0.4)" },
    cancelled: { c: "#FCA5A5", bg: "rgba(122,14,23,0.10)", bd: "rgba(122,14,23,0.4)" },
  }[s] || { c: "#888", bg: "#8882", bd: "#8885" });

  const filtered = statusFilter === "all" ? rows : rows.filter((r) => r.status === statusFilter);

  const mediumLabel = (m) => ({ whatsapp: "WhatsApp", zoom: "Zoom", meet: "Google Meet", phone: "Llamada telefónica" }[m] || m);
  const finalLabel = (f) => ({ attention_completed: "Atención completada", not_attended: "No asistió", rescheduled: "Reagendado" }[f] || f);

  return (
    <PageShell
      kicker="Consultor · Acompañamiento"
      title="MIS SESIONES ASIGNADAS"
      desc="Solicitudes que te fueron asignadas por el Administrador. Aceptalas, programá la sesión con el medio externo y registrá el cierre. La sesión se coordina fuera de KD9."
      testId="cons-page"
      badge={<span className="kd9-card px-4 py-2 text-[11px] font-mont tracking-widest uppercase" data-testid="cons-count">{filtered.length} sesion{filtered.length === 1 ? "" : "es"}</span>}
    >
      <div className="kd9-card p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="cons-filters">
        <div>
          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Estado</div>
          <select className="kd9-input pl-3 py-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="cons-filter-status">
            <option value="all">Todas</option>
            <option value="assigned">Consultor asignado</option>
            <option value="scheduled">Cita programada</option>
            <option value="in_progress">En proceso</option>
            <option value="completed">Atención finalizada</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg border text-sm font-mont" data-testid="cons-error"
          style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }}>{error}</div>
      )}
      {ok && (
        <div className="mb-4 px-4 py-3 rounded-lg border text-sm font-mont" data-testid="cons-ok"
          style={{ color: "#86EFAC", borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.08)" }}>{ok}</div>
      )}

      {loading ? (
        <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }} data-testid="cons-loading">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }} data-testid="cons-empty">
          No tenés sesiones para el filtro seleccionado.
        </div>
      ) : (
        <div className="kd9-card overflow-hidden" data-testid="cons-list">
          {filtered.map((r, i) => {
            const isOpen = openId === r.id;
            const tone = statusTone(r.status);
            const sd = sched[r.id] || {};
            const cd = comp[r.id] || {};
            return (
              <div key={r.id} className="border-b last:border-0" style={{ borderColor: "var(--kd9-border)" }} data-testid={`cons-row-${i}`}>
                <div className="p-5 grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-12 md:col-span-4">
                    <div className="font-mont font-semibold text-sm" data-testid={`cons-user-${i}`}>{r.user_name || r.user_email}</div>
                    <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>#{r.id.slice(-8)} · {r.user_email}</div>
                  </div>
                  <div className="col-span-6 md:col-span-3 font-mont text-[12px]">
                    <div>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</div>
                    <div style={{ color: "var(--kd9-text-soft)" }}>{r.session_type === "phone" ? "Llamada" : "Videollamada"} · {r.duration_min} min</div>
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold" data-testid={`cons-status-${i}`}
                      style={{ background: tone.bg, color: tone.c, border: `1px solid ${tone.bd}` }}>{statusLabel(r.status)}</span>
                  </div>
                  <div className="col-span-12 md:col-span-2 flex justify-end">
                    <button onClick={() => setOpenId(isOpen ? null : r.id)} className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] flex items-center gap-2" data-testid={`cons-toggle-${i}`}>
                      <Eye size={12} /> {isOpen ? "Cerrar" : "Ver / Gestionar"}
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="px-5 pb-5" data-testid={`cons-panel-${i}`}>
                    <div className="grid md:grid-cols-2 gap-5">
                      {/* Info del usuario */}
                      <div className="kd9-card p-4">
                        <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Motivo</div>
                        <p className="font-cormorant text-base" data-testid={`cons-reason-${i}`}>{r.reason}</p>

                        {r.notes && <>
                          <div className="text-[10px] font-mont tracking-widest uppercase mt-4 mb-1" style={{ color: "var(--kd9-text-soft)" }}>Observaciones del usuario</div>
                          <p className="font-cormorant text-base" data-testid={`cons-user-notes-${i}`}>{r.notes}</p>
                        </>}

                        {r.admin_observation && <>
                          <div className="text-[10px] font-mont tracking-widest uppercase mt-4 mb-1" style={{ color: "var(--kd9-text-soft)" }}>Observación del administrador</div>
                          <p className="font-cormorant text-base" data-testid={`cons-admin-obs-${i}`}>{r.admin_observation}</p>
                        </>}

                        {r.attachment_url && <>
                          <div className="text-[10px] font-mont tracking-widest uppercase mt-4 mb-1" style={{ color: "var(--kd9-text-soft)" }}>Archivo adjunto</div>
                          <a href={r.attachment_url} target="_blank" rel="noreferrer" className="kd9-gold-text font-mont text-sm underline break-all" data-testid={`cons-attach-${i}`}>{r.attachment_url}</a>
                        </>}

                        {Array.isArray(r.history) && r.history.length > 0 && (
                          <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--kd9-border)" }}>
                            <div className="text-[10px] font-mont tracking-widest uppercase mb-2" style={{ color: "var(--kd9-text-soft)" }}>Historial</div>
                            <ul className="space-y-1" data-testid={`cons-history-${i}`}>
                              {r.history.map((h, k) => (
                                <li key={k} className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
                                  · {new Date(h.at).toLocaleString()} — <strong style={{ color: "var(--kd9-text)" }}>{h.action}</strong>{h.actor_email && ` · ${h.actor_email}`}{h.scheduled_date && ` · ${h.scheduled_date} ${h.scheduled_time}`}{h.medium && ` · ${mediumLabel(h.medium)}`}{h.final_status && ` · ${finalLabel(h.final_status)}`}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Acciones */}
                      <div>
                        {r.status === "assigned" && (
                          <div className="kd9-card p-4 mb-3">
                            <div className="font-cinzel kd9-gold-text text-sm mb-3">DECISIÓN DEL CONSULTOR</div>
                            <div className="flex gap-2">
                              <button disabled={busy === r.id} onClick={() => accept(r.id)} className="kd9-btn-primary px-4 py-2 rounded-lg text-xs">Aceptar</button>
                              <button disabled={busy === r.id} onClick={() => reject(r.id)} className="kd9-btn-outline px-4 py-2 rounded-lg text-xs">Rechazar</button>
                            </div>
                          </div>
                        )}
                        {(r.status === "accepted" || r.status === "scheduled") && (
                          <div className="kd9-card p-4" data-testid={`cons-schedule-${i}`}>
                            <div className="font-cinzel kd9-gold-text text-sm tracking-widest mb-3">
                              {r.status === "scheduled" ? "REPROGRAMAR CITA" : "PROGRAMAR CITA"}
                            </div>
                            {r.status === "scheduled" && (
                              <div className="mb-3 text-[11px] font-mont p-2 rounded" style={{ background: "rgba(166,124,0,0.10)", color: "var(--kd9-text)" }}>
                                Programada: <strong>{r.scheduled_date} {r.scheduled_time}</strong> · {mediumLabel(r.medium)}
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div>
                                <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Fecha</div>
                                <input type="date" className="kd9-input pl-3 py-2 text-sm w-full" value={sd.date || r.scheduled_date || ""} onChange={(e) => setSched((p) => ({ ...p, [r.id]: { ...p[r.id], date: e.target.value } }))} data-testid={`cons-date-${i}`} />
                              </div>
                              <div>
                                <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Hora</div>
                                <input type="time" className="kd9-input pl-3 py-2 text-sm w-full" value={sd.time || r.scheduled_time || ""} onChange={(e) => setSched((p) => ({ ...p, [r.id]: { ...p[r.id], time: e.target.value } }))} data-testid={`cons-time-${i}`} />
                              </div>
                            </div>
                            <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Medio de atención</div>
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              {[
                                { v: "whatsapp", label: "WhatsApp", Icon: MessageSquare },
                                { v: "zoom", label: "Zoom", Icon: Video },
                                { v: "meet", label: "Google Meet", Icon: Video },
                                { v: "phone", label: "Llamada telefónica", Icon: Ticket },
                              ].map(({ v, label, Icon }) => {
                                const active = (sd.medium || r.medium) === v;
                                return (
                                  <button key={v} type="button" onClick={() => setSched((p) => ({ ...p, [r.id]: { ...p[r.id], medium: v } }))}
                                    className="px-3 py-2 rounded-lg text-[10px] font-mont tracking-widest uppercase font-semibold border flex items-center justify-center gap-2"
                                    style={{ borderColor: active ? "var(--kd9-gold)" : "var(--kd9-border)", background: active ? "rgba(212,175,55,0.10)" : "transparent", color: active ? "var(--kd9-gold)" : "var(--kd9-text)" }}
                                    data-testid={`cons-medium-${v}-${i}`}>
                                    <Icon size={12} /> {label}
                                  </button>
                                );
                              })}
                            </div>

                            <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Datos de contacto (según medio)</div>
                            <div className="grid grid-cols-1 gap-2 mb-3">
                              <input placeholder="Enlace Zoom (si aplica)" className="kd9-input pl-3 py-2 text-sm" value={sd.zoom_link || r.contact_info?.zoom_link || ""} onChange={(e) => setSched((p) => ({ ...p, [r.id]: { ...p[r.id], zoom_link: e.target.value } }))} data-testid={`cons-contact-zoom-${i}`} />
                              <input placeholder="Enlace Google Meet (si aplica)" className="kd9-input pl-3 py-2 text-sm" value={sd.meet_link || r.contact_info?.meet_link || ""} onChange={(e) => setSched((p) => ({ ...p, [r.id]: { ...p[r.id], meet_link: e.target.value } }))} data-testid={`cons-contact-meet-${i}`} />
                              <input placeholder="Usuario / número WhatsApp (si aplica)" className="kd9-input pl-3 py-2 text-sm" value={sd.whatsapp || r.contact_info?.whatsapp || ""} onChange={(e) => setSched((p) => ({ ...p, [r.id]: { ...p[r.id], whatsapp: e.target.value } }))} data-testid={`cons-contact-whatsapp-${i}`} />
                              <input placeholder="Número telefónico (si aplica)" className="kd9-input pl-3 py-2 text-sm" value={sd.phone || r.contact_info?.phone || ""} onChange={(e) => setSched((p) => ({ ...p, [r.id]: { ...p[r.id], phone: e.target.value } }))} data-testid={`cons-contact-phone-${i}`} />
                            </div>

                            <button onClick={() => schedule(r.id)} disabled={busy === r.id}
                              className="kd9-btn-primary w-full py-3 rounded-lg text-[11px] flex items-center justify-center gap-2"
                              style={{ opacity: busy === r.id ? 0.6 : 1 }} data-testid={`cons-schedule-submit-${i}`}>
                              <Calendar size={13} /> {busy === r.id ? "Guardando…" : (r.status === "scheduled" ? "Actualizar cita" : "Programar cita")}
                            </button>
                          </div>
                        )}

                        {r.status === "scheduled" && (
                          <div className="kd9-card p-4 mt-3" data-testid={`cons-complete-${i}`}>
                            <div className="font-cinzel kd9-gold-text text-sm tracking-widest mb-3">INICIAR ACOMPAÑAMIENTO</div>
                            <p className="font-cormorant text-base mb-4" style={{ color: "var(--kd9-text-soft)" }}>
                              Inicia la sesión al momento de atender al usuario. El cierre se habilitará cuando el acompañamiento esté en proceso.
                            </p>
                            <button onClick={() => start(r.id)} disabled={busy === r.id}
                              className="kd9-btn-primary w-full py-3 rounded-lg text-[11px] flex items-center justify-center gap-2"
                              style={{ opacity: busy === r.id ? 0.6 : 1 }} data-testid={`cons-start-${i}`}>
                              <Video size={13} /> {busy === r.id ? "Iniciando…" : "Iniciar sesión"}
                            </button>
                          </div>
                        )}

                        {r.status === "in_progress" && (
                          <div className="kd9-card p-4 mt-3" data-testid={`cons-complete-${i}`}>
                            <div className="font-cinzel kd9-gold-text text-sm tracking-widest mb-3">FINALIZAR ATENCIÓN</div>

                            <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Estado final</div>
                            <select className="kd9-input pl-3 py-2 text-sm w-full mb-3"
                              value={cd.final_status || "attention_completed"}
                              onChange={(e) => setComp((p) => ({ ...p, [r.id]: { ...p[r.id], final_status: e.target.value } }))}
                              data-testid={`cons-final-status-${i}`}>
                              <option value="attention_completed">Atención completada</option>
                              <option value="not_attended">No asistió</option>
                              <option value="rescheduled">Reagendado (queda cerrado)</option>
                            </select>

                            <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Observaciones terapéuticas</div>
                            <textarea rows={3} className="kd9-input pl-3 py-2 text-sm w-full mb-3" value={cd.therapy_notes || ""} onChange={(e) => setComp((p) => ({ ...p, [r.id]: { ...p[r.id], therapy_notes: e.target.value } }))} data-testid={`cons-therapy-notes-${i}`} placeholder="Resumen clínico visible en el registro…" />

                            <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Recomendaciones</div>
                            <textarea rows={2} className="kd9-input pl-3 py-2 text-sm w-full mb-3" value={cd.recommendations || ""} onChange={(e) => setComp((p) => ({ ...p, [r.id]: { ...p[r.id], recommendations: e.target.value } }))} data-testid={`cons-recommendations-${i}`} placeholder="Prácticas, ejercicios, próximos pasos…" />

                            <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Observaciones privadas (solo consultor)</div>
                            <textarea rows={2} className="kd9-input pl-3 py-2 text-sm w-full mb-3" value={cd.private_notes || ""} onChange={(e) => setComp((p) => ({ ...p, [r.id]: { ...p[r.id], private_notes: e.target.value } }))} data-testid={`cons-private-notes-${i}`} placeholder="Notas internas del consultor…" />

                            <button onClick={() => complete(r.id)} disabled={busy === r.id}
                              className="kd9-btn-primary w-full py-3 rounded-lg text-[11px] flex items-center justify-center gap-2"
                              style={{ opacity: busy === r.id ? 0.6 : 1 }} data-testid={`cons-complete-submit-${i}`}>
                              <CheckCircle2 size={13} /> {busy === r.id ? "Guardando…" : "Registrar cierre"}
                            </button>
                          </div>
                        )}

                        {r.status === "completed" && (
                          <div className="kd9-card p-4" style={{ background: "rgba(34,197,94,0.06)" }} data-testid={`cons-summary-${i}`}>
                            <div className="font-cinzel kd9-gold-text text-sm tracking-widest mb-3">ATENCIÓN CERRADA</div>
                            <div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Estado final</div>
                            <div className="font-mont font-semibold mb-3" data-testid={`cons-final-shown-${i}`}>{finalLabel(r.final_status)}</div>
                            {r.scheduled_date && <>
                              <div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Sesión</div>
                              <div className="font-mont text-sm mb-3">{r.scheduled_date} {r.scheduled_time} · {mediumLabel(r.medium)}</div>
                            </>}
                            {r.therapy_notes && <>
                              <div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Observaciones terapéuticas</div>
                              <p className="font-cormorant text-base mb-3" data-testid={`cons-shown-therapy-${i}`}>{r.therapy_notes}</p>
                            </>}
                            {r.recommendations && <>
                              <div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Recomendaciones</div>
                              <p className="font-cormorant text-base mb-3" data-testid={`cons-shown-reco-${i}`}>{r.recommendations}</p>
                            </>}
                            {r.private_notes && <>
                              <div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Observaciones privadas</div>
                              <p className="font-cormorant text-base" data-testid={`cons-shown-private-${i}`}>{r.private_notes}</p>
                            </>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
};

/* ============== ASIGNACIONES (Admin · Acompañamiento — Pantalla 2) ============== */
export const Asignaciones = () => {
  const [rows, setRows] = React.useState([]);
  const [consultants, setConsultants] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("pending_assignment");
  const [emailQuery, setEmailQuery] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [openId, setOpenId] = React.useState(null);
  const [pickConsultor, setPickConsultor] = React.useState({});
  const [obs, setObs] = React.useState({});
  const [busyId, setBusyId] = React.useState(null);
  const [okMsg, setOkMsg] = React.useState("");

  const reload = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (emailQuery.trim()) params.set("user_email", emailQuery.trim());
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const [reqRes, consRes] = await Promise.all([
        api.get(`/api/admin/acompanamiento/requests?${params.toString()}`),
        api.get(`/api/admin/acompanamiento/consultants`),
      ]);
      setRows(reqRes.data);
      setConsultants(consRes.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, emailQuery, dateFrom, dateTo]);

  React.useEffect(() => { reload(); }, [reload]);

  const doAssign = async (id) => {
    setError(""); setOkMsg("");
    const consultor_id = pickConsultor[id];
    if (!consultor_id) { setError("Seleccioná un consultor antes de asignar."); return; }
    setBusyId(id);
    try {
      const { data } = await api.post(`/api/admin/acompanamiento/${id}/assign`, {
        consultor_id,
        admin_observation: (obs[id] || "").trim() || undefined,
      });
      setOkMsg(`Solicitud #${data.id.slice(-8)} asignada a ${data.assigned_consultor_email}.`);
      setPickConsultor((p) => { const n = { ...p }; delete n[id]; return n; });
      setObs((o) => { const n = { ...o }; delete n[id]; return n; });
      setOpenId(null);
      await reload();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
  const statusLabel = (s) => ({
    requested: "Solicitado",
    pending_assignment: "Pendiente de asignación",
    assigned: "Asignado a Consultor",
    scheduled: "Agendado",
    in_progress: "En proceso",
    completed: "Completado",
    cancelled: "Cancelado",
  }[s] || s);

  return (
    <PageShell
      kicker="Administrador · Acompañamiento"
      title="ASIGNAR CONSULTOR"
      desc="Solicitudes de acompañamiento de los usuarios. Seleccioná un consultor aprobado y asigná la sesión. La sesión se coordinará por un medio externo (Zoom / WhatsApp / llamada)."
      testId="asignaciones-page"
      badge={<span className="kd9-card px-4 py-2 text-[11px] font-mont tracking-widest uppercase" data-testid="asig-count">{rows.length} solicitud{rows.length === 1 ? "" : "es"}</span>}
    >
      {/* Filtros */}
      <div className="kd9-card p-4 mb-6 grid md:grid-cols-4 gap-3" data-testid="asig-filters">
        <div>
          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Estado</div>
          <select className="kd9-input pl-3 py-2 text-sm w-full" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="asig-filter-status">
            <option value="pending_assignment">Pendiente de asignación</option>
            <option value="assigned">Consultor asignado</option>
            <option value="scheduled">Agendado</option>
            <option value="in_progress">En proceso</option>
            <option value="completed">Completado</option>
            <option value="cancelled">Cancelado</option>
            <option value="all">Todas</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Usuario (email)</div>
          <input className="kd9-input pl-3 py-2 text-sm w-full" placeholder="ej: maria@…" value={emailQuery} onChange={(e) => setEmailQuery(e.target.value)} data-testid="asig-filter-email" />
        </div>
        <div>
          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Desde</div>
          <input type="date" className="kd9-input pl-3 py-2 text-sm w-full" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="asig-filter-from" />
        </div>
        <div>
          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Hasta</div>
          <input type="date" className="kd9-input pl-3 py-2 text-sm w-full" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="asig-filter-to" />
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg border text-sm font-mont" data-testid="asig-error"
          style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }}>{error}</div>
      )}
      {okMsg && (
        <div className="mb-4 px-4 py-3 rounded-lg border text-sm font-mont" data-testid="asig-ok"
          style={{ color: "#86EFAC", borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.08)" }}>{okMsg}</div>
      )}

      {loading ? (
        <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }} data-testid="asig-loading">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }} data-testid="asig-empty">
          No hay solicitudes que coincidan con los filtros.
        </div>
      ) : (
        <div className="kd9-card overflow-hidden" data-testid="asig-list">
          {rows.map((r, i) => {
            const isOpen = openId === r.id;
            return (
              <div key={r.id} className="border-b last:border-0" style={{ borderColor: "var(--kd9-border)" }} data-testid={`asig-row-${i}`}>
                <div className="p-5 grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-12 md:col-span-3">
                    <div className="font-mont font-semibold text-sm" data-testid={`asig-user-${i}`}>{r.user_name || r.user_email}</div>
                    <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>#{r.id.slice(-8)} · {r.user_email}</div>
                  </div>
                  <div className="col-span-6 md:col-span-2 font-mont text-[12px]">
                    <div>{fmtDate(r.created_at)}</div>
                    <div style={{ color: "var(--kd9-text-soft)" }}>{fmtTime(r.created_at)}</div>
                  </div>
                  <div className="col-span-6 md:col-span-2 font-mont text-[12px]">
                    <div>{r.session_type === "phone" ? "Llamada" : "Videollamada"}</div>
                    <div style={{ color: "var(--kd9-text-soft)" }}>{r.duration_min} min</div>
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold" data-testid={`asig-status-${i}`}
                      style={{
                        background: r.status === "pending_assignment" ? "rgba(212,175,55,0.18)" : "rgba(15,76,129,0.20)",
                        color: r.status === "pending_assignment" ? "var(--kd9-gold)" : "#7BB8E8",
                        border: `1px solid ${r.status === "pending_assignment" ? "rgba(212,175,55,0.4)" : "rgba(15,76,129,0.5)"}`,
                      }}>{statusLabel(r.status)}</span>
                  </div>
                  <div className="col-span-6 md:col-span-2 flex justify-end">
                    <button onClick={() => setOpenId(isOpen ? null : r.id)} className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] flex items-center gap-2" data-testid={`asig-toggle-${i}`}>
                      <Eye size={12} /> {isOpen ? "Cerrar" : "Ver / Asignar"}
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="px-5 pb-5 pt-1" data-testid={`asig-panel-${i}`}>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Motivo</div>
                        <p className="font-cormorant text-base" data-testid={`asig-reason-${i}`}>{r.reason}</p>

                        {r.notes && <>
                          <div className="text-[10px] font-mont tracking-widest uppercase mt-4 mb-1" style={{ color: "var(--kd9-text-soft)" }}>Observaciones del usuario</div>
                          <p className="font-cormorant text-base" data-testid={`asig-notes-${i}`}>{r.notes}</p>
                        </>}

                        {r.attachment_url && <>
                          <div className="text-[10px] font-mont tracking-widest uppercase mt-4 mb-1" style={{ color: "var(--kd9-text-soft)" }}>Archivo adjunto</div>
                          <a href={r.attachment_url} target="_blank" rel="noreferrer" className="kd9-gold-text font-mont text-sm underline break-all" data-testid={`asig-attach-${i}`}>{r.attachment_url}</a>
                        </>}
                      </div>

                      <div>
                        {r.status === "pending_assignment" ? (
                          <>
                            <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Consultor aprobado *</div>
                            <select className="kd9-input pl-3 py-2 text-sm w-full mb-3"
                              value={pickConsultor[r.id] || ""}
                              onChange={(e) => setPickConsultor((p) => ({ ...p, [r.id]: e.target.value }))}
                              data-testid={`asig-consultor-${i}`}>
                              <option value="">Seleccionar consultor…</option>
                              {consultants
                                .filter((c) => (c.supported_session_types || []).includes(r.session_type))
                                .map((c) => (
                                  <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
                                ))}
                            </select>
                            {pickConsultor[r.id] && consultants.find((item) => item.id === pickConsultor[r.id])?.photo_url && (
                              <div className="flex items-center gap-3 mb-3 kd9-card p-3">
                                <img src={consultantPhotoSrc(consultants.find((item) => item.id === pickConsultor[r.id]).photo_url)} alt="Consultor seleccionado" className="w-16 h-16 rounded-xl object-cover" />
                                <span className="font-mont text-xs">{consultants.find((item) => item.id === pickConsultor[r.id]).name}</span>
                              </div>
                            )}
                            {consultants.filter((c) => (c.supported_session_types || []).includes(r.session_type)).length === 0 && (
                              <div className="mb-3 text-[11px] font-mont" style={{ color: "#FCA5A5" }} data-testid={`asig-no-cons-${i}`}>
                                No hay Consultores con esta modalidad habilitada por Dirección.
                              </div>
                            )}

                            <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Observación administrativa (opcional)</div>
                            <textarea rows={3} className="kd9-input pl-3 py-2 text-sm w-full mb-3"
                              value={obs[r.id] || ""}
                              onChange={(e) => setObs((o) => ({ ...o, [r.id]: e.target.value }))}
                              placeholder="Ej: preferencia horaria, contexto adicional para el consultor…"
                              data-testid={`asig-obs-${i}`} />

                            <button onClick={() => doAssign(r.id)} disabled={busyId === r.id || !pickConsultor[r.id]}
                              className="kd9-btn-primary w-full py-3 rounded-lg text-[11px] flex items-center justify-center gap-2"
                              style={{ opacity: (busyId === r.id || !pickConsultor[r.id]) ? 0.6 : 1 }}
                              data-testid={`asig-submit-${i}`}>
                              <UserCheck size={13} /> {busyId === r.id ? "Asignando…" : "Asignar consultor"}
                            </button>
                          </>
                        ) : (
                          <div className="kd9-card p-4" style={{ background: "rgba(15,76,129,0.08)" }}>
                            <div className="text-[10px] font-mont tracking-widest uppercase mb-2" style={{ color: "var(--kd9-text-soft)" }}>Consultor asignado</div>
                            {r.assigned_consultor_photo_url && <img src={consultantPhotoSrc(r.assigned_consultor_photo_url)} alt={r.assigned_consultor_name || "Consultor KD9"} className="w-20 h-20 rounded-xl object-cover mb-3" />}
                            <div className="font-mont font-semibold" data-testid={`asig-assigned-cons-${i}`}>{r.assigned_consultor_name}</div>
                            <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>{r.assigned_consultor_email}</div>
                            <div className="text-[10px] font-mont tracking-widest uppercase mt-4 mb-1" style={{ color: "var(--kd9-text-soft)" }}>Asignado por</div>
                            <div className="font-mont text-sm">{r.assigned_by_email} · {r.assigned_at ? new Date(r.assigned_at).toLocaleString() : "—"}</div>
                            {r.admin_observation && <>
                              <div className="text-[10px] font-mont tracking-widest uppercase mt-4 mb-1" style={{ color: "var(--kd9-text-soft)" }}>Observación administrativa</div>
                              <p className="font-cormorant text-base" data-testid={`asig-admin-obs-${i}`}>{r.admin_observation}</p>
                            </>}
                          </div>
                        )}

                        {Array.isArray(r.history) && r.history.length > 0 && (
                          <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--kd9-border)" }}>
                            <div className="text-[10px] font-mont tracking-widest uppercase mb-2" style={{ color: "var(--kd9-text-soft)" }}>Historial</div>
                            <ul className="space-y-1" data-testid={`asig-history-${i}`}>
                              {r.history.map((h, k) => (
                                <li key={k} className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
                                  · {new Date(h.at).toLocaleString()} — <strong style={{ color: "var(--kd9-text)" }}>{h.action}</strong> {h.actor_email && `· ${h.actor_email}`}{h.consultor_email && ` → ${h.consultor_email}`}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
};

/* ============== REVISIÓN DIAGNÓSTICOS (Admin) ============== */
export const RevisionDiagnosticos = () => {
  const { accessMap } = useAuth();
  const canApproveDelivery = (accessMap?.permissions || []).includes("diagnostics.delivery.approve");
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const [returnNotes, setReturnNotes] = React.useState({});
  const [deliveryNotes, setDeliveryNotes] = React.useState({});
  const [notice, setNotice] = React.useState("");
  const [version, setVersion] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/director/diagnostics/delivery-queue");
        if (!cancelled) { setRows(data); setError(""); }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [version]);

  const reload = () => setVersion((v) => v + 1);

  const approveDelivery = async (row) => {
    if (!row.review_token || !row.draft_revision) {
      setError("La versión aprobada no tiene token de entrega. Actualiza la cola antes de continuar.");
      return;
    }
    setBusy(row.id);
    setError("");
    setNotice("");
    try {
      await api.post(`/api/director/diagnostics/${row.id}/approve-delivery`, {
        notes: (deliveryNotes[row.id] || "").trim() || undefined,
        expected_review_token: row.review_token,
        expected_revision: row.draft_revision,
      });
      setDeliveryNotes((prev) => ({ ...prev, [row.id]: "" }));
      setNotice("Diagnóstico entregado y publicado al usuario.");
      window.dispatchEvent(new Event("kd9-diagnostics-changed"));
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "DELIVERY_STALE_SUBMISSION" || detail?.code === "DELIVERY_DECISION_CONFLICT") {
        setError("La versión cambió mientras confirmabas la entrega. No se publicó una versión obsoleta; la cola fue actualizada.");
      } else if (detail?.code === "DELIVERY_ALREADY_FINALIZED") {
        setError("Este diagnóstico ya fue entregado. La cola fue actualizada.");
      } else {
        setError(formatApiError(e));
      }
      reload();
    } finally {
      setBusy("");
    }
  };

  const returnToConsultant = async (row) => {
    const observation = (returnNotes[row.id] || "").trim();
    if (!observation) {
      setError("Para devolver un diagnóstico debes indicar exactamente qué debe corregir el consultor.");
      return;
    }
    if (!row.review_token || !row.draft_revision) {
      setError("La versión aprobada no tiene token de revisión. Actualiza la cola antes de continuar.");
      return;
    }
    setBusy(row.id);
    setError("");
    try {
      await api.post(`/api/admin/diagnostics/${row.id}/review-worker`, {
        action: "correction",
        observation,
        expected_review_token: row.review_token,
        expected_revision: row.draft_revision,
      });
      setReturnNotes((prev) => ({ ...prev, [row.id]: "" }));
      window.dispatchEvent(new Event("kd9-diagnostics-changed"));
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "REVIEW_STALE_SUBMISSION" || detail?.code === "REVIEW_DECISION_CONFLICT") {
        setError("El diagnóstico cambió mientras estaba abierto. No se aplicó la devolución; la cola fue actualizada.");
      } else {
        setError(formatApiError(e));
      }
      reload();
    } finally {
      setBusy("");
    }
  };

  return (
    <PageShell
      kicker="Director KD9 · Decisión final"
      title="ENTREGAS FINALES"
      desc="Solo aparecen estudios que ya aprobaron la revisión operativa. Desde aquí Dirección entrega o devuelve a corrección."
      testId="director-final-deliveries-page"
    >
      {notice && (
        <div className="kd9-card p-4 mb-4 text-sm font-mont"
          style={{ color: "#86EFAC", borderColor: "rgba(34,197,94,0.35)", background: "rgba(34,197,94,0.08)" }}>
          {notice}
        </div>
      )}
      {error && (
        <div className="kd9-card p-4 mb-4 text-sm font-mont"
          style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }}
          data-testid="revision-error">{error}</div>
      )}
      {rows.length === 0 && !error && (
        <div className="kd9-card p-10 text-center font-cormorant" style={{ color: "var(--kd9-text-soft)" }} data-testid="revision-empty">
          No hay entregas finales pendientes.
        </div>
      )}
      <div className="space-y-4">
        {rows.map((it, i) => (
          <article key={it.id} className="kd9-card p-5" data-testid={`revision-row-${i}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-mont font-semibold">{it.user_email || it.user_id?.slice(-6)}</div>
                <div className="text-[12px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
                  {it.catalog_title || it.catalog_slug} · #{it.id.slice(-8)}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest opacity-60">
                  Revisión aprobada {it.review_approved_at ? new Date(it.review_approved_at).toLocaleString() : ""} · versión {it.draft_revision}
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold"
                style={{ background: "rgba(168,85,247,.12)", color: "#C084FC", border: "1px solid rgba(168,85,247,.35)" }}>
                Lista para entrega
              </span>
            </div>

            <section className="mt-4 p-4 border rounded-xl" style={{borderColor:"var(--kd9-border)"}}>
              <div className="text-[10px] uppercase tracking-widest mb-2">Estudio aprobado por revisión</div>
              <div className="whitespace-pre-wrap font-cormorant text-lg">{it.study_content}</div>
              {it.worker_notes && <p className="mt-3 text-sm"><strong>Nota interna del consultor:</strong> {it.worker_notes}</p>}
              {(it.review_history || []).length > 0 && (
                <details className="mt-4 p-3 border rounded-lg" style={{borderColor:"var(--kd9-border)"}}>
                  <summary className="cursor-pointer text-xs font-semibold">Historial de revisión ({it.review_history.length})</summary>
                  <div className="mt-3 space-y-2">
                    {it.review_history.map((item,index)=><div key={index} className="text-xs opacity-80">
                      <strong>{item.action === "correction" ? "Devuelto" : "Aprobado"}</strong> · versión {item.submission_revision} · ronda {item.correction_round || 0}
                      {item.observation && <div className="mt-1 whitespace-pre-wrap">{item.observation}</div>}
                    </div>)}
                  </div>
                </details>
              )}
            </section>

            <textarea
              className="kd9-input mt-4 p-3 min-h-[80px] w-full"
              placeholder="Mensaje de entrega opcional para el usuario."
              value={deliveryNotes[it.id] || ""}
              onChange={(e)=>setDeliveryNotes((prev)=>({...prev,[it.id]:e.target.value}))}
            />
            <textarea
              className="kd9-input mt-3 p-3 min-h-[90px] w-full"
              placeholder="Solo si vas a devolver: explica la corrección requerida."
              value={returnNotes[it.id] || ""}
              onChange={(e)=>setReturnNotes((prev)=>({...prev,[it.id]:e.target.value}))}
            />

            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              {canApproveDelivery && (
                <button
                  disabled={busy===it.id}
                  onClick={()=>approveDelivery(it)}
                  className="kd9-btn-primary px-4 py-2 rounded-lg flex gap-2 justify-center items-center"
                  data-testid={`revision-approve-${i}`}
                >
                  <Check size={12}/> Entregar al usuario
                </button>
              )}
              <button
                disabled={busy===it.id}
                onClick={()=>returnToConsultant(it)}
                className="kd9-btn-outline px-4 py-2 rounded-lg flex gap-2 justify-center items-center"
              >
                <X size={12}/> Devolver al consultor
              </button>
            </div>
          </article>
        ))}
      </div>
    </PageShell>
  );
};

/* ============== PAGOS / COMPROBANTES (Admin + Director KD9) — WIRED ============== */
import api, { formatApiError } from "@/lib/api";

export const Pagos = ({ serviceType = "", flowTitle = "" }) => {
  const { role } = useRole();
  const { accessMap } = useAuth();
  const permissions = new Set(accessMap?.permissions || []);
  const canVerify = permissions.has("payments.verify");
  const canApproveDelegated = permissions.has("payments.approve_delegated");
  const canApprove = canVerify || canApproveDelegated;
  const canObserve = permissions.has("payments.view_review_only");
  const isDirector = role === "superadmin";

  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [version, setVersion] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const path = isDirector ? "/api/director/payments" : "/api/admin/payments/review-only";
        const { data } = await api.get(path, { params: serviceType ? { service_type: serviceType } : {} });
        if (!cancelled) { setRows(data); setError(""); }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [version, isDirector, serviceType]);

  const reload = () => setVersion((v) => v + 1);

  const observe = (id) => {
    const observation = window.prompt("Observación interna sobre el comprobante:", "");
    if (!observation) return;
    return api
      .post(`/api/admin/payments/${id}/observation`, { observation })
      .then(reload)
      .catch((e) => setError(formatApiError(e)));
  };

  const decide = (id, action) => {
    const notes = window.prompt(
      action === "approve" ? "Mensaje de aprobación para el usuario (obligatorio en diagnósticos):" : "Motivo del rechazo (opcional):",
      ""
    );
    if (notes === null) return;
    const payment = rows.find((row) => row.id === id);
    if (action === "approve" && payment?.service_type === "diagnostic" && !notes.trim()) {
      setError("Escribe el mensaje de aprobación que verá el usuario en Mis Diagnósticos.");
      return;
    }
    const endpoint = action === "approve" && canApproveDelegated && !canVerify
      ? `/api/admin/payments/${id}/approve`
      : `/api/director/payments/${id}/${action}`;
    return api
      .post(endpoint, { notes })
      .then(reload)
      .catch((e) => setError(formatApiError(e)));
  };

  const STATUS_LABEL = {
    pending_review: "En verificación",
    approved: "Aprobado",
    rejected: "Rechazado",
  };
  const tone = (s) => s === "approved" ? "#22C55E" : s === "rejected" ? "#FCA5A5" : "#D4AF37";
  const formatAmount = (a) => (a == null ? "—" : "$ " + new Intl.NumberFormat("es-CO").format(a));

  if (isDirector && !serviceType) {
    const flows = [
      ["/app/pagos-diagnosticos", "Pagos diagnósticos"],
      ["/app/pagos-academia", "Pagos academia"],
    ];
    return <PageShell kicker="Director KD9 · Tesorería" title="FLUJOS DE PAGOS" desc="Selecciona un módulo. No se presentan comprobantes ni estados mezclados.">{flows.map(([path, label]) => <a key={path} href={path} className="kd9-card mb-3 block p-5 font-cinzel kd9-gold-text">{label}</a>)}</PageShell>;
  }

  return (
    <PageShell
      kicker={isDirector ? "Director KD9 · Pagos" : "Administrador · Pagos (revisión)"}
      title={isDirector ? (flowTitle || "PAGOS Y COMPROBANTES") : "REVISIÓN DE PAGOS"}
      desc={isDirector
        ? `Flujo independiente de ${flowTitle || "pagos"}. Solo contiene su historial, estados y decisiones; cada acción genera auditoría y notificación KD9.`
        : canApproveDelegated
          ? "El Director te autorizó temporalmente para aprobar pagos. No puedes rechazar pagos ni delegar este permiso."
          : "Puedes revisar comprobantes y dejar observaciones internas. Aprobar o rechazar pagos requiere autorización del Director KD9."}
      testId="pagos-page"
      badge={!isDirector && (
        <span className="kd9-card px-3 py-2 text-[10px] font-mont tracking-widest uppercase" style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)" }} data-testid="pagos-readonly-badge">
          {canApproveDelegated ? "Autorizado por el Director · sólo aprobar" : "Solo revisión · sin aprobar/rechazar"}
        </span>
      )}
    >
      {error && (
        <div
          className="kd9-card p-4 mb-4 text-sm font-mont"
          style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }}
          data-testid="pagos-error"
        >
          {error}
        </div>
      )}

      <div className="kd9-card overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-6 py-3 text-[10px] font-mont tracking-widest uppercase border-b" style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text-soft)" }}>
          <div className="col-span-3">Usuario</div>
          <div className="col-span-3">Concepto</div>
          <div className="col-span-2">Monto</div>
          <div className="col-span-2">Estado</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>

        {rows.length === 0 && !loading && (
          <div className="p-10 text-center font-cormorant" style={{ color: "var(--kd9-text-soft)" }} data-testid="pagos-empty">
            No hay pagos para mostrar en este flujo.
          </div>
        )}

        {rows.map((p, i) => {
          const label = STATUS_LABEL[p.status] || p.status;
          const c = tone(p.status);
          const date = p.created_at ? new Date(p.created_at).toLocaleString() : "—";
          return (
            <div key={p.id} className="grid grid-cols-1 gap-4 px-4 py-5 border-b last:border-0 md:grid-cols-12 md:gap-3 md:px-6 md:py-4 md:items-center" style={{ borderColor: "var(--kd9-border)" }} data-testid={`pago-row-${i}`}>
              <div className="min-w-0 md:col-span-3">
                <div className="mb-1 text-[10px] font-mont tracking-widest uppercase md:hidden" style={{ color: "var(--kd9-text-soft)" }}>Usuario</div>
                <div className="font-mont font-semibold text-sm break-words">{p.user_email || p.user_id?.slice(-6)}</div>
                <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>#{p.id.slice(-6)} · {date}</div>
              </div>
              <div className="min-w-0 font-mont text-sm md:col-span-3">
                <div className="mb-1 text-[10px] tracking-widest uppercase md:hidden" style={{ color: "var(--kd9-text-soft)" }}>Concepto</div>
                <span className="break-words">{p.service_type}</span> · <span className="break-all" style={{ color: "var(--kd9-text-soft)" }}>{p.service_id}</span>
                {p.admin_observation && (
                  <div className="text-[11px] font-cormorant italic mt-1" style={{ color: "#93C5FD" }} data-testid={`pago-adminobs-${i}`}>
                    Admin: “{p.admin_observation}”
                  </div>
                )}
                {p.director_observation && (
                  <div className="text-[11px] font-cormorant italic mt-1" style={{ color: "var(--kd9-gold)" }} data-testid={`pago-dirobs-${i}`}>
                    Director: “{p.director_observation}”
                  </div>
                )}
              </div>
              <div className="font-cinzel kd9-gold-text text-base md:col-span-2"><span className="mr-2 text-[10px] font-mont tracking-widest uppercase md:hidden" style={{ color: "var(--kd9-text-soft)" }}>Monto</span>{formatAmount(p.amount)}</div>
              <div className="md:col-span-2">
                <div className="mb-2 text-[10px] font-mont tracking-widest uppercase md:hidden" style={{ color: "var(--kd9-text-soft)" }}>Estado</div>
                <span className="inline-flex px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold" style={{ background: `${c}22`, color: c, border: `1px solid ${c}55` }}>
                  {label}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 border-t pt-4 md:col-span-2 md:justify-end md:border-t-0 md:pt-0">
                <a
                  href={p.proof_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="min-h-10 flex-1 rounded-lg px-3 flex items-center justify-center gap-2 border font-mont text-[10px] uppercase tracking-wider md:h-8 md:min-h-0 md:w-8 md:flex-none md:px-0"
                  style={{ borderColor: "var(--kd9-border)" }}
                  data-testid={`pago-view-${i}`}
                  aria-label="Ver comprobante"
                >
                  <Receipt size={14} style={{ color: "var(--kd9-gold)" }} />
                  <span className="md:hidden">Ver comprobante</span>
                </a>
                {canObserve && !canApprove && p.status === "pending_review" && (
                  <button
                    onClick={() => observe(p.id)}
                    className="min-h-10 flex-1 rounded-lg px-3 flex items-center justify-center gap-2 font-mont text-[10px] uppercase tracking-wider md:h-8 md:min-h-0 md:w-8 md:flex-none md:px-0"
                    style={{ background: "rgba(60,120,181,0.18)", border: "1px solid rgba(60,120,181,0.45)" }}
                    data-testid={`pago-observe-${i}`}
                    aria-label="Dejar observación interna"
                    title="Dejar observación interna (no aprueba ni rechaza)"
                  >
                    <MessageSquare size={14} style={{ color: "#93C5FD" }} />
                    <span className="md:hidden">Observar</span>
                  </button>
                )}
                {canApprove && p.status === "pending_review" && (
                  <>
                    <button
                      onClick={() => decide(p.id, "approve")}
                      className="min-h-10 flex-1 rounded-lg px-3 flex items-center justify-center gap-2 font-mont text-[10px] uppercase tracking-wider md:h-8 md:min-h-0 md:w-8 md:flex-none md:px-0"
                      style={{ background: "rgba(22,163,74,0.2)", border: "1px solid rgba(22,163,74,0.4)" }}
                      data-testid={`pago-approve-${i}`}
                      aria-label="Aprobar"
                    >
                      <Check size={14} style={{ color: "#22C55E" }} />
                      <span className="md:hidden">Aprobar</span>
                    </button>
                    {canVerify && <button
                      onClick={() => decide(p.id, "reject")}
                      className="min-h-10 flex-1 rounded-lg px-3 flex items-center justify-center gap-2 font-mont text-[10px] uppercase tracking-wider md:h-8 md:min-h-0 md:w-8 md:flex-none md:px-0"
                      style={{ background: "rgba(122,14,23,0.2)", border: "1px solid rgba(122,14,23,0.4)" }}
                      data-testid={`pago-reject-${i}`}
                      aria-label="Rechazar"
                    >
                      <X size={14} style={{ color: "#FCA5A5" }} />
                      <span className="md:hidden">Rechazar</span>
                    </button>}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!canApprove && (
        <div className="kd9-card mt-5 p-4 flex items-start gap-3" style={{ background: "linear-gradient(135deg, rgba(122,14,23,0.10), transparent)" }} data-testid="pagos-admin-notice">
          <AlertTriangle size={16} style={{ color: "#FCA5A5" }} className="mt-0.5 shrink-0" />
          <p className="font-cormorant text-base" style={{ color: "var(--kd9-text)" }}>
            Vista de revisión para Administrador. Aprobar, rechazar y liberar cualquier acceso por pago es <strong>exclusivo del Director KD9</strong>.
          </p>
        </div>
      )}
    </PageShell>
  );
};

/* ============== ACADEMY PREMIUM — usuarios reales ============== */
export const Premium = () => {
  const { accessMap } = useAuth();
  const permissions = new Set(accessMap?.permissions || []);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [notice, setNotice] = React.useState({ type: "", text: "" });

  const academyPremiumEligible = (user) => !(user.roles || []).some((role) =>
    ["consultant", "creator", "support", "admin", "director"].includes(role)
  );

  const roleName = (user) => {
    const roles = user.roles || [];
    if (roles.includes("consultant")) return "Consultor";
    if (roles.includes("student")) return "Estudiante";
    if (roles.includes("creator")) return "Creador";
    if (roles.includes("support")) return "Soporte";
    return "Usuario";
  };

  const load = React.useCallback(async () => {
    setLoading(true);
    setNotice({ type: "", text: "" });
    try {
      const response = await api.get("/api/admin/users?limit=500");
      const source = Array.isArray(response.data) ? response.data : response.data?.items || [];
      setUsers(source.filter((user) => {
        const roles = user.roles || [];
        return !roles.includes("director") && !String(user.email || "").toLowerCase().endsWith("@kd9.local");
      }));
    } catch (error) {
      setNotice({ type: "error", text: formatApiError(error, "No fue posible cargar los usuarios.") });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const toggle = async (user) => {
    const active = Boolean(user.flags?.academy_premium);
    const allowed = academyPremiumEligible(user)
      && permissions.has(active ? "premium.deactivate" : "premium.activate");
    if (!allowed) return;
    if (!window.confirm(`¿${active ? "Desactivar" : "Activar"} Estudiante Premium para Academia para ${user.name || user.email}?`)) return;
    setBusy(user.id);
    setNotice({ type: "", text: "" });
    try {
      const response = await api.post(
        `/api/director/users/${user.id}/academy-premium/${active ? "deactivate" : "activate"}`
      );
      const updated = response.data?.user || response.data;
      if (updated?.id) setUsers((list) => list.map((item) => item.id === updated.id ? updated : item));
      else await load();
      setNotice({ type: "ok", text: `Estudiante Premium ${active ? "desactivado" : "activado"} correctamente.` });
    } catch (error) {
      setNotice({ type: "error", text: formatApiError(error, "No fue posible cambiar Premium de Academia.") });
    } finally {
      setBusy("");
    }
  };

  const needle = query.trim().toLowerCase();
  const visible = users.filter((user) =>
    !needle || [user.name, user.email, roleName(user)].filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle))
  );

  return (
    <PageShell
      kicker="Director KD9 · Academia"
      title="ACADEMIA PREMIUM"
      desc="Estudiante Premium es un beneficio académico independiente de las capacidades profesionales del Consultor."
    >
      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <input type="search" value={query} onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar nombre, correo o rol"
          className="min-h-12 flex-1 rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-amber-400" />
        <button type="button" onClick={load} disabled={loading}
          className="min-h-12 rounded-2xl border border-amber-300 px-5 text-sm font-semibold text-amber-800 disabled:opacity-50">
          {loading ? "Actualizando…" : "Actualizar"}
        </button>
      </div>
      {notice.text ? <div className={`mb-4 rounded-2xl border px-4 py-3 text-sm ${notice.type === "error" ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{notice.text}</div> : null}
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
        {loading ? <p className="p-8 text-center text-sm text-slate-500">Cargando usuarios reales…</p> :
          visible.length === 0 ? <p className="p-8 text-center text-sm text-slate-500">No hay usuarios para mostrar.</p> :
          visible.map((user) => {
            const active = Boolean(user.flags?.academy_premium);
            const allowed = academyPremiumEligible(user)
              && permissions.has(active ? "premium.deactivate" : "premium.activate");
            return <div key={user.id} className="flex items-center justify-between gap-4 border-b border-slate-100 p-5 last:border-0">
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-900">{user.name || "Sin nombre"}</p>
                <p className="truncate text-sm text-slate-500">{user.email}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-slate-400">{roleName(user)}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`hidden text-xs font-semibold uppercase tracking-wider sm:block ${active ? "text-amber-700" : "text-slate-500"}`}>
                  {active ? "Estudiante Premium" : (allowed ? "Sin premium" : "No aplica")}
                </span>
                <button type="button" role="switch" aria-checked={active}
                  aria-label={`${active ? "Desactivar" : "Activar"} Premium de Academia para ${user.name || user.email}`}
                  onClick={() => toggle(user)} disabled={busy === user.id || !allowed}
                  className={`relative h-8 w-14 rounded-full transition disabled:cursor-not-allowed disabled:opacity-50 ${active ? "bg-amber-500" : "bg-slate-200"}`}>
                  <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${active ? "translate-x-1" : "-translate-x-5"}`} />
                </button>
              </div>
            </div>;
          })}
      </div>
    </PageShell>
  );
};

/* ============== SUBIR PRODUCTO (Creador) ============== */
export const SubirProducto = () => (
  <PageShell kicker="Creador · Marketplace" title="SUBIR PRODUCTO"
    desc="Crea un producto para el Marketplace KD9. Todos los productos pasan por revisión antes de publicarse."
    testId="subir-producto-page"
  >
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <form className="kd9-card p-8 space-y-5" onSubmit={(e) => e.preventDefault()} data-testid="subir-producto-form">
        <div>
          <label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Nombre del producto</label>
          <input className="kd9-input pl-4" placeholder="Aceite Abundancia Divina" data-testid="producto-nombre" />
        </div>
        <div>
          <label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Descripción</label>
          <textarea rows={5} className="kd9-input pl-4 resize-none" placeholder="Describe la energía, intención y beneficios del producto…" data-testid="producto-desc" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Precio (COP)</label>
            <input className="kd9-input pl-4" placeholder="45000" data-testid="producto-precio" />
          </div>
          <div>
            <label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Categoría</label>
            <select className="kd9-input pl-4" data-testid="producto-categoria">
              <option>Aceites</option><option>Tisanas</option><option>Velas</option><option>Jabones</option><option>Sahumerios</option><option>Manillas</option><option>Otros</option>
            </select>
          </div>
        </div>
        <div>
          <label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Imágenes</label>
          <div className="border-2 border-dashed rounded-xl p-8 text-center" style={{ borderColor: "var(--kd9-border-strong)" }} data-testid="producto-uploader">
            <ImageIcon size={28} className="mx-auto mb-3" style={{ color: "var(--kd9-accent)" }} />
            <div className="font-mont text-sm mb-2">Arrastra tus imágenes aquí</div>
            <div className="font-mont text-[11px]" style={{ color: "var(--kd9-text-soft)" }}>PNG, JPG hasta 5MB cada una</div>
            <button className="kd9-btn-outline mt-4 px-5 py-2 rounded-lg text-[10px]">Seleccionar archivos</button>
          </div>
        </div>
        <button type="submit" className="kd9-btn-primary w-full py-4 rounded-xl text-[12px] flex items-center justify-center gap-3" data-testid="producto-enviar">
          <Send size={14} /> Enviar a revisión
        </button>
      </form>
      <aside className="kd9-card p-6 h-fit">
        <h3 className="font-cinzel kd9-gold-text text-lg tracking-widest mb-4 flex items-center gap-2">
          <AlertCircle size={16} style={{ color: "var(--kd9-gold)" }} /> Flujo de publicación
        </h3>
        {[
          "Subes el producto",
          "Pasa a revisión administrativa",
          "Si se aprueba, se publica en Marketplace",
          "Si requiere ajustes, recibirás retroalimentación",
        ].map((s, i) => (
          <div key={i} className="flex gap-3 mb-3">
            <div className="w-6 h-6 rounded-full flex items-center justify-center font-cinzel kd9-gold-text text-xs shrink-0" style={{ background: "var(--kd9-accent-soft)", border: "1px solid var(--kd9-border)" }}>{i + 1}</div>
            <div className="font-cormorant text-base" style={{ color: "var(--kd9-text)" }}>{s}</div>
          </div>
        ))}
      </aside>
    </div>
  </PageShell>
);

/* ============== TICKETS (Soporte / Admin) ============== */
export const Tickets = () => {
  const TICKETS = [
    { id: "TK-3021", user: "Camila Soto", subject: "No puedo subir comprobante", priority: "alta", status: "abierto", date: "Hace 12 min" },
    { id: "TK-3020", user: "Daniel Pérez", subject: "Consulta sobre curso bloqueado", priority: "media", status: "abierto", date: "Hace 1 h" },
    { id: "TK-3019", user: "Andrea Rojas", subject: "Cambio de correo", priority: "baja", status: "escalado", date: "Hace 3 h" },
    { id: "TK-3018", user: "Roberto Díaz", subject: "No recibe diagnóstico", priority: "alta", status: "escalado", date: "Hace 6 h" },
  ];
  const ptone = (p) => p === "alta" ? "#7A0E17" : p === "media" ? "#D4AF37" : "#3C78B5";
  return (
    <PageShell kicker="Soporte KD9" title="TICKETS DE SOPORTE" desc="Atiende casos. Si requieren aprobación financiera, escala al Director KD9." testId="tickets-page">
      <div className="kd9-card overflow-hidden">
        {TICKETS.map((tk, i) => (
          <div key={tk.id} className="p-5 grid grid-cols-12 gap-3 items-center border-b last:border-0" style={{ borderColor: "var(--kd9-border)" }} data-testid={`ticket-row-${i}`}>
            <div className="col-span-12 md:col-span-5">
              <div className="font-mont font-semibold text-sm">{tk.subject}</div>
              <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
                {tk.user} · #{tk.id} · {tk.date}
              </div>
            </div>
            <div className="col-span-3 md:col-span-2">
              <span className="px-2 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold" style={{ background: `${ptone(tk.priority)}22`, color: ptone(tk.priority), border: `1px solid ${ptone(tk.priority)}55` }}>
                {tk.priority}
              </span>
            </div>
            <div className="col-span-3 md:col-span-2 text-[11px] font-mont uppercase tracking-widest" style={{ color: tk.status === "escalado" ? "#A855F7" : "var(--kd9-success)" }}>
              {tk.status}
            </div>
            <div className="col-span-6 md:col-span-3 flex justify-end gap-2">
              <button className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px]" data-testid={`ticket-respond-${i}`}>Responder</button>
              <button className="px-3 py-2 rounded-lg text-[10px] font-mont uppercase tracking-widest font-semibold" style={{ background: "rgba(168,85,247,0.18)", color: "#C4B5FD", border: "1px solid rgba(168,85,247,0.4)" }} data-testid={`ticket-escalate-${i}`}>
                <AlertTriangle size={11} className="inline mr-1" /> Escalar
              </button>
            </div>
          </div>
        ))}
      </div>
    </PageShell>
  );
};

/* ============== COMPROBANTE UPLOAD (User payment) — WIRED ============== */
export const ComprobanteUpload = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedServiceId = searchParams.get("service_id") || "";
  const [serviceType, setServiceType] = React.useState(searchParams.get("service_type") || "diagnostic");
  const [serviceId, setServiceId] = React.useState(linkedServiceId);
  const [proofFile, setProofFile] = React.useState(null);
  const [amount, setAmount] = React.useState(searchParams.get("amount") || "");
  const linkedDiagnostic = serviceType === "diagnostic" && Boolean(linkedServiceId);
  const [reference, setReference] = React.useState("");
  const [method, setMethod] = React.useState("");
  const [paymentMethods, setPaymentMethods] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [progress, setProgress] = React.useState("");

  React.useEffect(() => {
    api.get("/api/payments/methods").then(({data}) => {
      setPaymentMethods(data.methods || []);
      if (!method && data.methods?.length) setMethod(data.methods[0].label);
    }).catch((e) => setError(formatApiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!proofFile) return setError("Selecciona la foto o PDF del comprobante.");
    if (!serviceId) return setError("No se encontró la solicitud asociada al pago.");
    if (proofFile.size > 12 * 1024 * 1024) return setError("El comprobante supera 12 MB.");
    setSubmitting(true);
    try {
      setProgress("Subiendo comprobante…");
      const filePayload = new FormData();
      filePayload.append("file", proofFile);
      const { data: stored } = await api.post("/api/payments/proof-file", filePayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProgress("Registrando pago para revisión…");
      await api.post("/api/payments/upload-proof", {
        service_type: serviceType,
        service_id: serviceId,
        proof_file_url: stored.url,
        amount: amount ? Number(amount) : undefined,
        reference: reference || undefined,
        method,
      });
      const destinations = {
        diagnostic: `/app/mis-diagnosticos?payment=submitted&focus=${encodeURIComponent(serviceId)}`,
        course: "/app/mis-cursos?payment=submitted",
        marketplace: "/app/mis-compras-marketplace?payment=submitted",
        accompaniment: "/app/acompanamiento?payment=submitted",
      };
      navigate(destinations[serviceType] || "/app/mis-pagos", { replace: true });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false); setProgress("");
    }
  };

  return (
    <PageShell kicker="Pago · Verificación" title="SUBIR COMPROBANTE DE PAGO"
      desc="Adjunta directamente la foto o PDF. Al enviarlo, te llevaremos al estado de tu diagnóstico."
      testId="comprobante-upload-page">
      <div className="max-w-2xl mx-auto">
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {paymentMethods.map((item) => (
            <button type="button" key={item.id} onClick={() => setMethod(item.label)}
              className="kd9-card p-5 text-left"
              style={{borderColor: method === item.label ? "var(--kd9-gold)" : "var(--kd9-border)"}}>
              <div className="font-cinzel kd9-gold-text">{item.label}</div>
              {item.bank_name && <div className="text-sm mt-2">{item.bank_name} {item.account_type}</div>}
              {item.account_number && <div className="text-sm">Cuenta: <strong>{item.account_number}</strong></div>}
              {item.account_holder && <div className="text-xs">Titular: {item.account_holder}</div>}
              {item.phone && <div className="text-xs">Teléfono: {item.phone}</div>}
              {item.qr_image_url && <img src={item.qr_image_url} alt={`QR ${item.label}`} className="w-40 h-40 object-contain bg-white rounded-lg mt-3 mx-auto"/>}
            </button>
          ))}
        </div>

        {error && <div className="kd9-card p-4 mb-4 text-sm" style={{color:"#FCA5A5"}}>{error}</div>}
        <form noValidate onSubmit={submit} className="kd9-card p-6">
          <div className="mb-5">
            <label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Foto o PDF del comprobante</label>
            <input type="file" accept="image/*,.heic,.heif,.pdf" className="block w-full text-sm"
              onChange={(e)=>setProofFile(e.target.files?.[0] || null)} data-testid="comprobante-file"/>
            {proofFile && <div className="mt-2 text-xs" style={{color:"#22C55E"}}>{proofFile.name}</div>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Tipo de servicio</label>
              <select className="kd9-input pl-4" value={serviceType} onChange={(e) => setServiceType(e.target.value)} disabled={linkedDiagnostic}>
                <option value="diagnostic">Diagnóstico</option><option value="course">Curso</option><option value="marketplace">Marketplace</option><option value="accompaniment">Acompañamiento</option>
              </select>
            </div>
            <div>
              <label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Solicitud vinculada</label>
              <input className="kd9-input pl-4" value={serviceId} onChange={(e) => setServiceId(e.target.value)} disabled={linkedDiagnostic}/>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div><label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Monto (COP)</label><input type="number" className="kd9-input pl-4" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={linkedDiagnostic}/></div>
            <div><label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Método de pago</label><select className="kd9-input pl-4" value={method} onChange={(e) => setMethod(e.target.value)}>{paymentMethods.map((item)=><option key={item.id} value={item.label}>{item.label}</option>)}</select></div>
          </div>

          <div className="mb-6"><label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Referencia / Número</label><input className="kd9-input pl-4" placeholder="Ej: 1234567890" value={reference} onChange={(e) => setReference(e.target.value)}/></div>
          <button type="submit" disabled={submitting} className="kd9-btn-primary w-full py-4 rounded-xl text-[12px]">{submitting ? progress || "Enviando…" : "Enviar comprobante y ver estado"}</button>
        </form>
        <div className="kd9-card mt-5 p-5 flex items-start gap-3"><Clock size={18} style={{ color: "var(--kd9-accent)" }} className="mt-0.5 shrink-0" /><p className="font-cormorant text-base">Tu comprobante quedará en revisión. Al enviarlo verás inmediatamente el estado de tu diagnóstico.</p></div>
      </div>
    </PageShell>
  );
};

/* ============== Generic stub for less critical pages ============== */
export const StubPage = ({ title, kicker, desc, icon: Icon = Sparkles }) => (
  <PageShell kicker={kicker} title={title} desc={desc} testId="stub-page">
    <div className="kd9-card p-12 text-center kd9-fade-up">
      <div
        className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6"
        style={{
          background: "var(--kd9-accent-soft)",
          border: "1px solid var(--kd9-border-strong)",
        }}
      >
        <Icon size={28} style={{ color: "var(--kd9-accent)" }} />
      </div>
      <h3 className="font-cinzel kd9-gold-text text-2xl tracking-widest mb-3">
        SECCIÓN EN CONSTRUCCIÓN
      </h3>
      <p className="font-cormorant text-lg max-w-xl mx-auto" style={{ color: "var(--kd9-text-soft)" }}>
        Esta vista forma parte del flujo visual KD9 y se conectará con datos reales en la próxima fase. El layout, permisos y navegación ya están definidos.
      </p>
    </div>
  </PageShell>
);
